import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  CircularProgress,
  Typography,
  IconButton,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { adsService } from '../services/ads.service';

interface Props {
  open: boolean;
  onClose: () => void;
  adId: string;
}

export default function CreativePreviewDialog({ open, onClose, adId }: Props) {
  const [loading, setLoading] = useState(true);
  const [media, setMedia] = useState<{
    thumbnail_url: string | null;
    image_url: string | null;
    video_id: string | null;
    video_source: string | null;
    video_thumbnail_urls: string[];
  } | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!open || !adId) return;
    setLoading(true);
    setError(false);
    adsService.getMedia(adId)
      .then((data) => setMedia({
        thumbnail_url: data.thumbnail_url || null,
        image_url: data.image_url || null,
        video_id: data.video_id || null,
        video_source: data.video_source || null,
        video_thumbnail_urls: data.video_thumbnail_urls || [],
      }))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [open, adId]);

  const imageUrl = media?.image_url || media?.thumbnail_url;
  const isVideo = !!media?.video_id;
  const videoSource = media?.video_source;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { bgcolor: 'black', overflow: 'hidden' } }}
    >
      <IconButton
        onClick={onClose}
        sx={{ position: 'absolute', top: 8, right: 8, zIndex: 10, color: 'white', bgcolor: 'rgba(0,0,0,0.5)' }}
      >
        <Close />
      </IconButton>

      <DialogContent sx={{ p: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 400 }}>
        {loading && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, py: 8 }}>
            <CircularProgress sx={{ color: 'white' }} />
            <Typography color="grey.400" variant="body2">Loading from Facebook...</Typography>
          </Box>
        )}

        {/* No stand-in: the stored thumbnail is 64x64, and enlarged it reads as the creative. */}
        {!loading && error && (
          <Box sx={{ py: 8, px: 4, textAlign: 'center' }}>
            <Typography color="grey.400">Could not load this creative from Facebook.</Typography>
          </Box>
        )}

        {!loading && !error && media && (
          <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            {/* Dynamic creative: show all video variant thumbnails */}
            {media.video_thumbnail_urls.length > 0 ? (
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'center', p: 2 }}>
                {media.video_thumbnail_urls.map((url, i) => (
                  <Box
                    key={i}
                    component="img"
                    src={url}
                    alt={`Video variant ${i + 1}`}
                    sx={{ maxHeight: 400, maxWidth: '48%', objectFit: 'contain', borderRadius: 1 }}
                  />
                ))}
              </Box>
            ) : isVideo && videoSource ? (
              /* Video ad with stream URL: show playable video */
              <Box
                component="video"
                src={videoSource}
                poster={imageUrl || undefined}
                controls
                autoPlay
                sx={{ maxWidth: '100%', maxHeight: 600, objectFit: 'contain' }}
              />
            ) : imageUrl ? (
              <Box
                component="img"
                src={imageUrl}
                alt="Creative"
                sx={{ maxWidth: '100%', maxHeight: 600, objectFit: 'contain' }}
              />
            ) : (
              <Typography color="grey.400" sx={{ py: 8 }}>This ad has no creative image.</Typography>
            )}

            {/* Info bar */}
            <Box sx={{ p: 1.5, width: '100%', bgcolor: 'rgba(255,255,255,0.05)', display: 'flex', gap: 2, justifyContent: 'center' }}>
              {isVideo && (
                <Typography variant="caption" color="grey.400">
                  Video ID: {media.video_id}
                </Typography>
              )}
              {media.video_thumbnail_urls.length > 1 && (
                <Typography variant="caption" color="grey.400">
                  {media.video_thumbnail_urls.length} video variants (dynamic creative)
                </Typography>
              )}
            </Box>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}
